import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { PaginationResponse } from "@/core/services/api/interfaces/PaginationResponse";
import ApiService from "@/core/services/ApiService";
import { AxiosRequestConfig } from "axios";
import { isUserAdmin } from "@/core/services/UserRoleService";
import { AnnouncementExtended } from "@/core/services/api/interfaces/manager/Announcement";

@Module
export default class AnnouncementsModule extends VuexModule {
  pagination = new PaginationResponse<AnnouncementExtended>();
  loading = false;

  get announcementList(): AnnouncementExtended[] {
    return this.announcementListPagination.data;
  }

  get announcementItem() {
    return (id: string): null | AnnouncementExtended => {
      for (const i in this.pagination.data) {
        const announcement = this.pagination.data[i];
        if (Number(id) === announcement.id) {
          return announcement;
        }
      }

      return null;
    };
  }

  get announcementListLoading(): boolean {
    return this.loading;
  }

  get announcementListPagination(): PaginationResponse<AnnouncementExtended> {
    return this.pagination;
  }

  get announcementListTotal(): number {
    return this.pagination.total;
  }

  get announcementListRequired(): boolean {
    return !this.pagination.total && !this.loading;
  }

  get announcementListCurrentPage(): number {
    return this.pagination.current_page;
  }

  get announcementListPerPage(): number {
    return this.pagination.per_page;
  }

  @Action({ rawError: true })
  [Actions.ADMIN_ANNOUNCEMENTS_CREATE](params) {
    if (!isUserAdmin()) {
      return;
    }

    return ApiService.admin.announcement.create(params);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_ANNOUNCEMENTS_UPDATE](params) {
    if (!isUserAdmin()) {
      return;
    }

    return ApiService.admin.announcement
      .update(params)
      .then(ApiService.unwrapItemResponse)
      .then((data) =>
        this.context.commit(Mutations.ADMIN_ANNOUNCEMENTS_REPLACE, data)
      );
  }

  @Action({ rawError: true })
  [Actions.ADMIN_ANNOUNCEMENTS_DELETE](announce) {
    if (!isUserAdmin()) {
      return;
    }

    return ApiService.admin.announcement.delete(announce);
  }

  @Action({ rawError: true })
  [Actions.ADMIN_ANNOUNCEMENTS_VIEW](id: string) {
    return ApiService.admin.announcement
      .view(id)
      .then(ApiService.unwrapItemResponse);
  }
  @Action({ rawError: true })
  [Actions.ADMIN_ANNOUNCEMENTS_LOAD](params) {
    if (!isUserAdmin()) {
      return;
    }
    this.context.commit(Mutations.ADMIN_ANNOUNCEMENTS_SET_LOADING, true);

    return ApiService.admin.announcement
      .list({
        params: params,
      } as AxiosRequestConfig)
      .then(ApiService.unwrapPaginationResponse)
      .then((response) => {
        this.context.commit(Mutations.ADMIN_ANNOUNCEMENTS_SET, response);

        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.ADMIN_ANNOUNCEMENTS_LOAD
        );
      })
      .catch(console.error)
      .finally(() =>
        this.context.commit(Mutations.ADMIN_ANNOUNCEMENTS_SET_LOADING, false)
      );
  }

  @Mutation
  [Mutations.ADMIN_ANNOUNCEMENTS_SET](
    pagination: PaginationResponse<AnnouncementExtended>
  ) {
    this.pagination = pagination;
  }

  @Mutation
  [Mutations.ADMIN_ANNOUNCEMENTS_RESET]() {
    this.pagination = new PaginationResponse<AnnouncementExtended>();
  }

  @Mutation
  [Mutations.ADMIN_ANNOUNCEMENTS_SET_LOADING](loading: boolean) {
    this.loading = loading;
  }

  @Mutation
  [Mutations.ADMIN_ANNOUNCEMENTS_REPLACE](withAnnouncement) {
    for (const i in this.pagination.data) {
      const announcement = this.pagination.data[i];
      if (announcement.id !== withAnnouncement.id) {
        continue;
      }
      this.pagination.data[i] = Object.assign(
        this.pagination.data[i],
        withAnnouncement
      );
    }
  }

  @Mutation
  [Mutations.ADMIN_ANNOUNCEMENTS_REMOVE](ofAnnouncement) {
    for (const i in this.pagination.data) {
      const announcement = this.pagination.data[i];
      if (announcement.id !== ofAnnouncement.id) {
        continue;
      }

      delete this.pagination.data[i];
    }
  }
}
